// Las filas de la pestaña Documentos: cinco fijas de KYC y dos por crédito. Cada fila dice de qué
// archivo habla, y la que no tiene archivo dice qué falta y por qué, sin enlaces a los que ir.
import type { LoanContractResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanContractResponse';
import { ContractTypeEnum } from '@fiado/type-kit/bin/loanCredit/enums/ContractTypeEnum';
import type {
    BlockStatus,
    CustomerDocumentsResponse,
    LegalAcceptance,
    LegalDocumentLinkStatus,
    OnboardingDocument,
} from '@/api/services/retail-customer/retail-customer.client';
import { formatDateMx } from '@/lib/dateHelpers';
import {
    CAT_COVER_NO_SOURCE,
    DOCUMENT_LINK_UNSIGNED,
    DOCUMENTS_WITHOUT_BODY,
    type Gap,
} from './customer360Gaps';
import type { ReadFailure } from './creditCustomersErrors';

/** La autorización al buró no se captura ni se firma en ningún flujo: no existe como dato. */
const BUREAU_AUTHORIZATION_NO_SOURCE: Gap = {
    label: 'No existe como dato',
    note: 'La autorización de consulta al buró no existe como dato en ningún silo: no se captura ni se firma en ningún flujo del producto. El diseño la muestra siempre «firmada digital», y eso no se puede afirmar de nadie.',
};

/** Lo que se pinta en el tramo del tamaño mientras ninguna fuente lo devuelva. */
const NO_SIZE = '—';

const ID_CARD_NAME = 'INE frente + reverso';
const PROOF_OF_ADDRESS_NAME = 'Comprobante de domicilio';
const PRIVACY_NOTICE_NAME = 'Aviso de privacidad';
const CREDIT_CONTRACT_NAME = 'Contrato de crédito';

/** Una acción de la fila. El endpoint da UNA sola URL por archivo: ver y descargar van a la misma. */
export interface DocumentRowLink {
    label: string;
    href: string;
}

/** Una fila de archivo. Sin `links` la fila va apagada y su `subLine` dice qué falta. */
export interface DocumentRow {
    key: string;
    name: string;
    /** `Firmado 17/jun/2026 · 12 KB · <creditId>`, o el motivo corto cuando no hay archivo. */
    subLine: string;
    links: DocumentRowLink[];
    /** La explicación larga del motivo, para el `title`. `null` cuando la fila tiene archivo. */
    note: string | null;
}

/* ================================================================================
   MOTIVOS PROPIOS DE ESTA PESTAÑA — cada ausencia con SU causa.
   ================================================================================ */

/** La validación con RENAPO deja un veredicto en la ficha, no un archivo en el expediente. */
const CURP_FILE_NO_SOURCE: Gap = {
    label: 'La validación no deja archivo',
    note: 'La CURP se valida contra RENAPO durante el KYC y el resultado se guarda como dato en la ficha del cliente. No se genera ningún archivo que descargar: el expediente no tiene constancia de esa validación.',
};

/** El asiento de aceptación existe; el PDF del aviso todavía no lo emite ningún proceso. */
const PRIVACY_NOTICE_PDF_PENDING =
    'La aceptación del aviso está asentada con su fecha y su versión, pero el documento en PDF todavía no lo genera ningún proceso. Se puede afirmar que el cliente aceptó; no se puede entregar el archivo que aceptó.';

/** Por qué el asiento no trae enlace al PDF. Los cuatro llevan a acciones distintas del operador. */
const PRIVACY_DOCUMENT_GAP: Record<LegalDocumentLinkStatus, Gap> = {
    OK: {
        label: 'El enlace llegó vacío',
        note: 'El silo dice que el documento aceptado está publicado y aun así no mandó el enlace para abrirlo. El PDF existe: lo que llegó incompleto es la respuesta.',
    },
    ABSENT: {
        label: 'Sin aviso vigente publicado',
        note: 'El tipo de aviso que este cliente aceptó no tiene un documento vigente publicado. Es lo que respondió la fuente, no una lectura que falló: no hay PDF que entregar.',
    },
    UNAVAILABLE: {
        label: 'El PDF no se pudo leer',
        note: 'El documento que este cliente aceptó existe y su lectura falló. NO es que no haya aviso: se puede reintentar volviendo a abrir la ficha.',
    },
    NO_SOURCE: {
        label: 'El silo todavía no lo sirve',
        note: 'El método que entrega el PDF del aviso todavía no está publicado en la librería con la que los lambdas se llaman entre sí. La aceptación sí se leyó; el documento no se llegó a pedir.',
    },
};

/** El expediente se leyó bien y el archivo no está: es un dato del expediente, no una falla. */
const NO_ID_FILES: Gap = {
    label: 'Sin identificación en el expediente',
    note: 'La lectura salió bien y el expediente de este cliente no tiene ni el frente ni el reverso de su identificación: su verificación nunca llegó a subirlos.',
};

const NO_PROOF_OF_ADDRESS_FILE: Gap = {
    label: 'Sin comprobante en el expediente',
    note: 'La lectura salió bien y el expediente de este cliente no tiene comprobante de domicilio: el paso que lo sube no llegó a cerrarse.',
};

const NO_PRIVACY_ACCEPTANCE: Gap = {
    label: 'Sin aceptación asentada',
    note: 'La lectura salió bien y este cliente no tiene ninguna aceptación de aviso de privacidad asentada. El consentimiento se registra al darlo de alta, así que su ausencia es un alta vieja.',
};

/** Mientras la lectura está en camino no se puede afirmar que el archivo falte. */
const READING_DOCUMENTS: Gap = {
    label: 'Consultando el expediente…',
    note: 'La lectura del expediente todavía está en camino. Hasta que vuelva no se puede decir si este archivo está o no.',
};

const READING_CONTRACTS: Gap = {
    label: 'Consultando los contratos…',
    note: 'La lectura de los contratos de este crédito todavía está en camino.',
};

/** El contrato está firmado y el enlace que lo abre todavía no lo sirve ningún endpoint. */
const CONTRACT_LINK_PENDING =
    'El PDF de este contrato está guardado y el endpoint que firma su enlace temporal todavía se está construyendo en el silo del crédito. Por eso la fila no tiene a dónde llevarte.';

const NO_CREDIT_CONTRACT: Gap = {
    label: 'Sin contrato emitido',
    note: 'La lectura salió bien y este crédito no tiene contrato de crédito emitido: se emite al originarlo, así que su ausencia es un crédito que no llegó a esa estación.',
};

const UNSIGNED_CREDIT_CONTRACT: Gap = {
    label: 'Borrador sin firmar',
    note: 'El contrato de este crédito está emitido y todavía no se firma: el silo no le anotó fecha de firma, que es la que se escribe al verificar el código del cliente.',
};

const CONTRACTS_FAILURE_LABEL: Record<ReadFailure, string> = {
    forbidden: 'Sin permiso a los contratos',
    'not-found': 'El crédito ya no está en el silo',
    error: 'Falló la lectura de los contratos',
};

const CONTRACTS_FAILURE_NOTE: Record<ReadFailure, string> = {
    forbidden:
        'Tu rol no incluye la lectura de los créditos, así que los contratos de este ni se pidieron. Contacta a quien administra los roles.',
    'not-found':
        'El silo del crédito no encuentra este crédito, así que no devolvió sus contratos. La lectura salió bien.',
    error: 'La lectura de los contratos de este crédito falló. No se sabe si tiene contrato o no; se puede reintentar volviendo a abrir la ficha.',
};

/** El hueco de una fila contractual cuando la lectura de los contratos no salió. */
export function contractsFailureGap(failure: ReadFailure): Gap {
    return { label: CONTRACTS_FAILURE_LABEL[failure], note: CONTRACTS_FAILURE_NOTE[failure] };
}

/* ================================================================================
   CLASIFICACIÓN DEL EXPEDIENTE — por el NOMBRE primero, por el `kind` después.
   ================================================================================ */

/** A qué fila del expediente va cada archivo. Lo que no cae en ninguna no se lista. */
export type OnboardingSlot = 'ID_FRONT' | 'ID_BACK' | 'PROOF_OF_ADDRESS';

/** Cómo nombra el silo de identidad al comprobante de domicilio, en sus variantes reales. */
const PROOF_OF_ADDRESS_PATTERNS = ['PROOF-OF-RESIDENCY', 'ADDRESS_DOCUMENT', 'ADDRESS-DOCUMENT'];

/**
 * A qué fila va un archivo del expediente. El silo viejo tipa por el ÚLTIMO tramo del nombre, así
 * que un `..._PROOF-OF-RESIDENCY_FRONT.jpeg` le sale identificación: por eso el nombre manda.
 */
export function onboardingSlotOf(doc: OnboardingDocument): OnboardingSlot | null {
    const name = doc.name.toUpperCase();
    if (PROOF_OF_ADDRESS_PATTERNS.some((pattern) => name.includes(pattern))) {
        return 'PROOF_OF_ADDRESS';
    }
    switch (doc.kind) {
        case 'ID_FRONT':
        case 'ID_BACK':
        case 'PROOF_OF_ADDRESS':
            return doc.kind;
        default:
            return null;
    }
}

/** El primer archivo de esa fila, en orden alfabético para que dos lecturas elijan el mismo. */
function documentOf(
    documents: OnboardingDocument[],
    slot: OnboardingSlot,
): OnboardingDocument | null {
    const matches = documents
        .filter((doc) => onboardingSlotOf(doc) === slot)
        .sort((a, b) => a.name.localeCompare(b.name));
    return matches.at(0) ?? null;
}

/** La aceptación del aviso. Los tipos reales son `PRIVACY_*`; `PRIVACY_NOTICE` no existe. */
export function privacyAcceptanceOf(acceptances: LegalAcceptance[]): LegalAcceptance | null {
    const privacy = acceptances.filter((acceptance) =>
        acceptance.type.toUpperCase().startsWith('PRIVACY'),
    );
    // La más reciente es la que rige; sin fecha, el tipo desempata para no elegir al azar.
    return (
        [...privacy].sort(
            (a, b) =>
                (b.acceptedAt ?? '').localeCompare(a.acceptedAt ?? '') ||
                a.type.localeCompare(b.type),
        )[0] ?? null
    );
}

/* ================================================================================
   SUBLÍNEAS — el hecho, su fecha y el tamaño. Nunca un «Firmado» que nadie firmó.
   ================================================================================ */

/** El peso del archivo. Identity lo manda ya convertido a KB; este silo todavía no lo compone. */
export function fileSizeLabel(sizeKb: number | null | undefined): string {
    return sizeKb === null || sizeKb === undefined ? NO_SIZE : `${String(sizeKb)} KB`;
}

/**
 * La sublínea de un archivo del expediente. `lastModified` dice cuándo se SUBIÓ el archivo, no
 * cuándo se firmó; sin él solo queda el alta del cliente, y se rotula como lo que es.
 */
function onboardingSubLine(
    customerCreatedAt: string | null,
    doc: OnboardingDocument | null,
): string {
    const when = onboardingDateLabel(customerCreatedAt, doc);
    return `${when} · ${fileSizeLabel(doc?.sizeKb)}`;
}

function onboardingDateLabel(
    customerCreatedAt: string | null,
    doc: OnboardingDocument | null,
): string {
    if (doc?.lastModified) return `Subido ${formatDateMx(doc.lastModified)}`;
    if (customerCreatedAt) return `Alta del cliente ${formatDateMx(customerCreatedAt)}`;
    return 'Sin fecha de alta';
}

/** Un hueco pintado como fila: el motivo corto abajo del nombre y el largo en el `title`. */
function gapRow(key: string, name: string, gap: Gap, reference?: string): DocumentRow {
    return {
        key,
        name,
        subLine: reference ? `${gap.label} · ${reference}` : gap.label,
        links: [],
        note: gap.note,
    };
}

/** Las dos acciones de un archivo con enlace. El endpoint da una sola URL: las dos van a ella. */
function fileLinks(url: string): DocumentRowLink[] {
    return [
        { label: 'Ver PDF', href: url },
        { label: 'Descargar', href: url },
    ];
}

/* ================================================================================
   KYC · ONBOARDING — cinco filas fijas, existan o no sus archivos.
   ================================================================================ */

export interface KycDocumentsInput {
    documents: CustomerDocumentsResponse | undefined;
    isLoading: boolean;
    failure: ReadFailure | null;
    /** El alta del cliente: la única fecha que esta pestaña puede ponerle al expediente. */
    customerCreatedAt: string | null;
}

/** Por qué el bloque entero no se puede leer todavía. `null` cuando sí llegó con archivos. */
function onboardingReadGap(
    input: KycDocumentsInput,
    block: 'onboarding' | 'privacyNotice',
): Gap | null {
    if (input.isLoading) return READING_DOCUMENTS;
    if (input.failure) return documentsFailureGap(input.failure);
    if (!input.documents) return DOCUMENTS_WITHOUT_BODY;
    return block === 'onboarding'
        ? onboardingBlockGap(input.documents.blocks.onboarding)
        : privacyNoticeBlockGap(input.documents.blocks.privacyNotice);
}

/**
 * El INE va en UNA fila con sus dos caras: son dos archivos y una sola credencial. Cada acción
 * abre la cara que nombra, en vez de mandar las dos al mismo enlace.
 */
function idCardRow(input: KycDocumentsInput): DocumentRow {
    const gap = onboardingReadGap(input, 'onboarding');
    if (gap) return gapRow('id-card', ID_CARD_NAME, gap);

    const documents = input.documents?.onboarding ?? [];
    const front = documentOf(documents, 'ID_FRONT');
    const back = documentOf(documents, 'ID_BACK');
    if (!front && !back) return gapRow('id-card', ID_CARD_NAME, NO_ID_FILES);

    const links: DocumentRowLink[] = [];
    if (front?.url) links.push({ label: 'Ver frente', href: front.url });
    if (back?.url) links.push({ label: 'Ver reverso', href: back.url });
    if (links.length === 0) return gapRow('id-card', ID_CARD_NAME, DOCUMENT_LINK_UNSIGNED);

    return {
        key: 'id-card',
        name: ID_CARD_NAME,
        subLine: onboardingSubLine(input.customerCreatedAt, front ?? back),
        links,
        note: null,
    };
}

function proofOfAddressRow(input: KycDocumentsInput): DocumentRow {
    const gap = onboardingReadGap(input, 'onboarding');
    if (gap) return gapRow('proof-of-address', PROOF_OF_ADDRESS_NAME, gap);

    const proof = documentOf(input.documents?.onboarding ?? [], 'PROOF_OF_ADDRESS');
    if (!proof) return gapRow('proof-of-address', PROOF_OF_ADDRESS_NAME, NO_PROOF_OF_ADDRESS_FILE);
    if (!proof.url)
        return gapRow('proof-of-address', PROOF_OF_ADDRESS_NAME, DOCUMENT_LINK_UNSIGNED);

    return {
        key: 'proof-of-address',
        name: PROOF_OF_ADDRESS_NAME,
        subLine: onboardingSubLine(input.customerCreatedAt, proof),
        links: fileLinks(proof.url),
        note: null,
    };
}

/** La aceptación se puede afirmar con su fecha; el archivo que se aceptó todavía no existe. */
function privacyNoticeRow(input: KycDocumentsInput): DocumentRow {
    const gap = onboardingReadGap(input, 'privacyNotice');
    if (gap) return gapRow('privacy-notice', PRIVACY_NOTICE_NAME, gap);

    const acceptance = privacyAcceptanceOf(input.documents?.privacyNotice ?? []);
    if (!acceptance) return gapRow('privacy-notice', PRIVACY_NOTICE_NAME, NO_PRIVACY_ACCEPTANCE);

    const when = acceptance.acceptedAt
        ? `Aceptado ${formatDateMx(acceptance.acceptedAt)}`
        : 'Aceptado sin fecha anotada';
    const accepted = `${when} · ${NO_SIZE}`;
    if (acceptance.documentUrl) {
        return {
            key: 'privacy-notice',
            name: PRIVACY_NOTICE_NAME,
            subLine: accepted,
            links: fileLinks(acceptance.documentUrl),
            note: null,
        };
    }

    // Sin `documentLinkStatus` la respuesta es la del silo todavía desplegado, que no manda el PDF.
    const linkGap = acceptance.documentLinkStatus
        ? PRIVACY_DOCUMENT_GAP[acceptance.documentLinkStatus]
        : null;
    return {
        key: 'privacy-notice',
        name: PRIVACY_NOTICE_NAME,
        subLine: linkGap ? `${accepted} · ${linkGap.label}` : accepted,
        links: [],
        note: linkGap ? linkGap.note : PRIVACY_NOTICE_PDF_PENDING,
    };
}

/** Las cinco filas del expediente, siempre las cinco y siempre en este orden. */
export function buildKycDocumentRows(input: KycDocumentsInput): DocumentRow[] {
    return [
        gapRow('curp', 'CURP renaproc validada', CURP_FILE_NO_SOURCE),
        idCardRow(input),
        proofOfAddressRow(input),
        privacyNoticeRow(input),
        gapRow('bureau', 'Autorización de consulta a buró', BUREAU_AUTHORIZATION_NO_SOURCE),
    ];
}

/* ================================================================================
   CONTRACTUALES · POR CRÉDITO — dos filas por cada crédito del cliente.
   ================================================================================ */

/** Cómo quedó la lectura de los contratos de UN crédito. */
export interface CreditContractsRead {
    contracts: LoanContractResponse[] | undefined;
    isLoading: boolean;
    failure: ReadFailure | null;
}

/**
 * El enlace temporal al PDF de un contrato. Hoy es `null` siempre: el silo guarda la llave en S3 y
 * todavía no expone el endpoint que la firma.
 */
export function contractUrlOf(_contract: LoanContractResponse): string | null {
    return null;
}

function creditContractRow(creditId: string, read: CreditContractsRead): DocumentRow {
    const key = `contract-${creditId}`;
    if (read.isLoading) return gapRow(key, CREDIT_CONTRACT_NAME, READING_CONTRACTS, creditId);
    if (read.failure) {
        return gapRow(key, CREDIT_CONTRACT_NAME, contractsFailureGap(read.failure), creditId);
    }

    const contract = (read.contracts ?? []).find((item) => item.type === ContractTypeEnum.CREDIT);
    if (!contract) return gapRow(key, CREDIT_CONTRACT_NAME, NO_CREDIT_CONTRACT, creditId);
    if (!contract.signedAt) {
        return gapRow(key, CREDIT_CONTRACT_NAME, UNSIGNED_CREDIT_CONTRACT, creditId);
    }

    const subLine = `Firmado ${formatDateMx(contract.signedAt)} · ${NO_SIZE} · ${creditId}`;
    const url = contractUrlOf(contract);
    if (!url) {
        return { key, name: CREDIT_CONTRACT_NAME, subLine, links: [], note: CONTRACT_LINK_PENDING };
    }
    return { key, name: CREDIT_CONTRACT_NAME, subLine, links: fileLinks(url), note: null };
}

export interface ContractDocumentsInput {
    /** Los créditos del cliente. Vacío = no originó ninguno y la sección no se pinta. */
    creditIds: string[];
    readsByCredit: Map<string, CreditContractsRead>;
}

const PENDING_CONTRACTS_READ: CreditContractsRead = {
    contracts: undefined,
    isLoading: true,
    failure: null,
};

/** Dos filas por crédito: el contrato y la carátula, en el orden del mock. */
export function buildContractDocumentRows(input: ContractDocumentsInput): DocumentRow[] {
    return input.creditIds.flatMap((creditId) => [
        creditContractRow(creditId, input.readsByCredit.get(creditId) ?? PENDING_CONTRACTS_READ),
        gapRow(`cat-${creditId}`, 'Carátula CAT', CAT_COVER_NO_SOURCE, creditId),
    ]);
}

/** Por qué no se sabe qué créditos tiene el cliente: la lectura de sus créditos no salió. */
export type UnreadCreditsState = 'loading' | 'no-source' | 'error' | 'forbidden';

const UNREAD_CREDITS_GAP: Record<Exclude<UnreadCreditsState, 'loading'>, Gap> = {
    'no-source': {
        label: 'El cliente no cruzó al crédito',
        note: 'Este cliente no tiene acreditado en el silo del crédito, que es de donde cuelgan los contratos. Sin ese cruce no hay a quién pedirle ninguno.',
    },
    error: {
        label: 'No se leyeron los créditos',
        note: 'La lectura de los créditos de este cliente falló, así que no se sabe cuáles tiene ni qué contratos firmó. La falla es de la lectura, no del cliente.',
    },
    forbidden: {
        label: 'Sin permiso a los créditos',
        note: 'Tu rol no incluye la lectura de los créditos de los clientes, así que sus contratos ni se pidieron. Contacta a quien administra los roles.',
    },
};

/**
 * La fila que evita que la sección quede muda cuando no se sabe qué créditos hay. En `loading`
 * devuelve `null`: la sección aparece cuando la lectura vuelve, sin afirmar nada mientras tanto.
 */
export function unreadContractsRow(state: UnreadCreditsState): DocumentRow | null {
    if (state === 'loading') return null;
    return gapRow('contracts-unread', 'Contratos del crédito', UNREAD_CREDITS_GAP[state]);
}

/** El meta de la sección: cuántos archivos de verdad tiene. Una fila apagada no es un archivo. */
export function documentFileCountLabel(rows: DocumentRow[]): string {
    const count = rows.filter((row) => row.links.length > 0).length;
    return count === 1 ? '1 archivo' : `${String(count)} archivos`;
}

/* ================================================================================
   VEREDICTOS DE LOS BLOQUES — el silo compone cada uno desde otro dueño.
   ================================================================================ */

/**
 * Por qué el EXPEDIENTE de identidad viene vacío. `null` cuando trae archivos: `DEGRADED` también
 * los trae, solo que sin enlace, y eso lo dice cada archivo por su cuenta.
 */
export function onboardingBlockGap(status: BlockStatus): Gap | null {
    if (status === 'OK' || status === 'DEGRADED') return null;
    if (status === 'ABSENT') {
        return {
            label: 'Sin archivos en el expediente',
            note: 'La lectura salió bien y el expediente de identidad de este cliente no tiene ningún archivo: su verificación nunca llegó a subir el documento ni la selfie.',
        };
    }
    if (status === 'NO_SOURCE') {
        return {
            label: 'No cruzó al directorio',
            note: 'Este cliente no tiene directorio en Fiado, que es de donde cuelga el expediente de identidad. Sin ese cruce no hay a quién pedirle sus archivos.',
        };
    }
    return {
        label: 'No se leyó el expediente',
        note: 'No se pudo leer el expediente de identidad de este cliente. La falla es de la lectura, no del expediente: no se sabe si tiene archivos o no.',
    };
}

/** Lo mismo para el AVISO DE PRIVACIDAD, que es otro bloque y se cae por su cuenta. */
export function privacyNoticeBlockGap(status: BlockStatus): Gap | null {
    if (status === 'OK' || status === 'DEGRADED') return null;
    if (status === 'ABSENT') {
        return {
            label: 'Sin aceptación registrada',
            note: 'La lectura salió bien y no hay ninguna aceptación de aviso de privacidad asentada para este cliente. El consentimiento se registra al darlo de alta, así que su ausencia es un alta vieja.',
        };
    }
    if (status === 'NO_SOURCE') {
        return {
            label: 'Sin directorio no hay asiento',
            note: 'Las aceptaciones legales se asientan contra el directorio de Fiado y este cliente no tiene uno, así que no hay dónde buscar su consentimiento.',
        };
    }
    return {
        label: 'No se leyeron las aceptaciones',
        note: 'No se pudo leer el registro de aceptaciones legales de este cliente. La falla es de la lectura: no se sabe si aceptó el aviso o no.',
    };
}

const DOCUMENTS_FAILURE_LABEL: Record<ReadFailure, string> = {
    forbidden: 'Sin permiso a los documentos',
    'not-found': 'La ficha ya no está en el silo',
    error: 'Falló la lectura de documentos',
};

const DOCUMENTS_FAILURE_NOTE: Record<ReadFailure, string> = {
    forbidden:
        'Tu rol no incluye los documentos de onboarding de los clientes, así que ni se pidieron. Es un permiso aparte del de la ficha; contacta a quien administra los roles.',
    'not-found':
        'El silo no encuentra a este cliente, así que no devolvió sus documentos de onboarding. La lectura salió bien.',
    error: 'La lectura de los documentos de onboarding falló. Los contratos del crédito se leyeron aparte y pueden estar bien.',
};

/** El hueco de la pestaña cuando la lectura entera de los documentos no salió. */
export function documentsFailureGap(failure: ReadFailure): Gap {
    return { label: DOCUMENTS_FAILURE_LABEL[failure], note: DOCUMENTS_FAILURE_NOTE[failure] };
}
